import type { Graphics } from 'pixi.js';
import type { Rng } from '../core/rng';
import type { BurstStyle } from '../core/skillfx';
import type { Enemy } from './entities';
import { warnBand, warnCircle, warnRing, warnSector } from './warn';

/**
 * 1.22 보스 셋 — 받은 그림의 동작을 보고 짠 기술들. 전부 바닥 경고(warn.ts)를 먼저 보여 주고, 경고가 다 차오르면 친다.
 *
 *  관문의 파수꾼 (요괴2, 월도 거한)  — 4:00
 *    휘두르기: 월도를 들고 버티다(원 경고) 한 바퀴 휘둘러 둘레 전부 · 찌르기: 웅크렸다(띠 경고) 월도를 쭉 뻗으며 앞으로 내딛는다 ·
 *    내려찍기: 멀면 뛰어올라 플레이어 자리에 떨어진다(떨어질 자리 원 경고) — 흙이 튄다
 *  홍련나찰 (여섯 팔의 검귀)          — 8:00
 *    칼바람: 칼을 돌리며(원 경고) 다가온다 · 매혹: 붉은 하트 셋을 부채꼴로 — 맞으면 끌려가서 칼바람 속으로(맞으면 바로 칼바람이 온다) ·
 *    다연 찌르기: 세 갈래 띠 · 베기: 가까우면 부채꼴 · 체력 절반·5분의 1 에서 「붉은 꽃의 회전무」 — 칼 고리 셋이 퍼진다, 빈틈으로 빠져나간다
 *  류대신 (핏빛 칼의 검신, 사람 크기)  — 12:00 (1.23 — 아래 Ryu)
 *  공손찬 (천하를 삼키는 괴왕)         — 16:00 (1.23 에 류대신이 끼어들어 12:00 → 16:00)
 *    화염 초승달: 넓은 부채꼴 · 불기둥: 발밑부터 차례로 솟는다 · 불구슬: 둘레를 돌다 사방으로 · 이무기: 가로 띠를 따라 화면을 가로지른다.
 *    체력 절반부터 더 잦고 불기둥이 늘어난다. 1.23: 체력 절반 아래·2바퀴째부터 「화우」 — 둘레에 불기둥이 비처럼 차례로
 *  (1.23 파수꾼: 「균열」 — 월도를 꽂으면 부채꼴로 금이 뻗어 터진다. 체력 절반 아래·2바퀴째부터 내려찍기 뒤 X 자 여진)
 *
 * 바퀴(lap): 보스는 4분마다 차례로 다시 나온다(16분부터 2바퀴, 28분부터 3바퀴). 바퀴마다 기술 사이 쉬는 시간이 줄고(1 / (1 + 0.3 × 바퀴))
 *   가짓수가 는다 — 파수꾼 내려찍기가 이어지고, 나찰 칼 고리·공손찬 불기둥·불구슬 물결이 는다. 한 방의 세기는 그대로(적 피해 배율만 시간 따라).
 *   → 30분 무렵부터는 경고를 읽고 빈틈으로 빠져나가는 실력이 있어야 버틴다
 *
 * 움직임·기술은 전부 판의 난수(rng)로 고른다(같은 시드는 같은 판). 그림만 Math.random 을 쓴다.
 */

export interface ShotSpec {
  x: number;
  y: number;
  vx: number;
  vy: number;
  damage: number;
  radius: number;
  life: number;
  /** fx 그림 이름 (없으면 둥근 탄) */
  art: string | null;
  scale?: number;
  /** 맞으면 [매혹] 이만큼(초) — 쏜 적 쪽으로 끌려간다 */
  charm?: number;
  src?: Enemy;
  /** 초당 이만큼(rad) 플레이어 쪽으로 휜다 */
  homing?: number;
  /** 맞은 자리에 터지는 그림 */
  hitFx?: string;
  /** 그림이 도는 빠르기(rad/s). 없으면 날아가는 쪽을 본다 */
  spin?: number;
  /** 이만큼(초) 동안 쏜 적 둘레를 돌다가 날아간다(공손찬 불구슬) */
  orbit?: number;
  orbitR?: number;
  orbitA?: number;
  orbitW?: number;
  /** 날아갈 때 빠르기 (orbit 이 끝난 뒤) */
  speed?: number;
  /** 기울이지 않고 좌우만 뒤집는다 (하트) */
  upright?: boolean;
}

export interface FxSpec {
  name: string;
  frame?: number;
  x: number;
  y: number;
  life: number;
  scale?: number;
  /** 끝날 때 크기 (없으면 그대로) */
  scale1?: number;
  rot?: number;
  flipX?: boolean;
  vx?: number;
  vy?: number;
  /** 바닥 쪽(발밑) 기준 — 불기둥처럼 아래에서 솟는 그림 */
  anchorBottom?: boolean;
  alpha?: number;
  /** 색을 입힌다 (1.24 기본 대시는 희푸르게 — 전격 질주는 원래 보랏빛 그대로) */
  tint?: number;
}

export interface BossHost {
  readonly px: number;
  readonly py: number;
  readonly rng: Rng;
  /** 이번 스텝 경고 그림 (스텝마다 지워진다) */
  readonly warn: Graphics;
  readonly playerCharmed: boolean;
  hurtIfIn(x: number, y: number, r: number, dmg: number): boolean;
  hurtIfBand(x: number, y: number, dir: number, len: number, hw: number, dmg: number): boolean;
  hurtIfSector(x: number, y: number, r: number, dir: number, half: number, dmg: number): boolean;
  enemyShot(spec: ShotSpec): void;
  fx(spec: FxSpec): void;
  /** 이번 스텝만 그리는 그림 (칼 고리처럼 매 스텝 자리가 바뀌는 것). sy 를 주면 세로 배율을 따로 (혈룡파 광선처럼 길게 늘일 때) */
  stamp(name: string, frame: number, x: number, y: number, rot: number, scale: number, alpha: number, sy?: number): void;
  burst(x: number, y: number, radius: number, attr: string, style?: BurstStyle, delay?: number): void;
  shakeScreen(t: number, mag: number): void;
  flashScreen(sec: number, alpha: number, tint: number): void;
  sound(name: string, rate?: number, gain?: number): void;
}

/** 보스의 머리 — 움직임과 기술. 그릴 동작도 정한다 */
export abstract class BossBrain {
  anim = 'move';
  /** 동작 안의 시간 — 이 시간 × fps 번째 장 */
  animT = 0;
  loop = true;
  /** 1 오른쪽, -1 왼쪽 */
  face = 1;
  /** 뛰어오른 높이 (그림만) */
  lift = 0;
  /** 공중에 있어 몸에 닿아도 안 아프다 */
  airborne = false;
  protected state = 'walk';
  protected t = 0;
  /** 첫 기술은 등장 띠가 지나간 뒤에 */
  protected cd = 2;
  protected last = '';
  /** 몇 바퀴째 (0 = 첫 등장) */
  lap = 0;

  /** 기술 사이 쉬는 시간 배율 — 바퀴마다 짧아진다 */
  protected get pace(): number {
    return 1 / (1 + 0.3 * this.lap);
  }

  abstract update(h: BossHost, e: Enemy, dt: number): void;

  protected enter(state: string): void {
    this.state = state;
    this.t = 0;
    this.last = state;
  }

  protected play(anim: string, loop: boolean): void {
    if (this.anim !== anim) {
      this.anim = anim;
      this.animT = 0;
    }
    this.loop = loop;
  }

  protected faceTo(dx: number): void {
    if (Math.abs(dx) > 6) this.face = dx > 0 ? 1 : -1;
  }

  /** 플레이어 쪽으로 걷는다 — stop 보다 가까우면 멈춘다 */
  protected walk(h: BossHost, e: Enemy, dt: number, speedK = 1, stop = 0): void {
    const dx = h.px - e.x;
    const dy = h.py - e.y;
    const d = Math.hypot(dx, dy) || 1;
    this.faceTo(dx);
    if (d > stop) {
      e.x += (dx / d) * e.speed * speedK * dt;
      e.y += (dy / d) * e.speed * speedK * dt;
    }
  }

  protected pick(h: BossHost, options: Array<[string, number]>): string {
    // 같은 기술을 두 번 잇지 않게 (가능하면)
    const pool = options.filter(([n, w]) => w > 0 && n !== this.last);
    const list = pool.length ? pool : options;
    const total = list.reduce((s, [, w]) => s + w, 0);
    let r = h.rng.next() * total;
    for (const [n, w] of list) {
      r -= w;
      if (r <= 0) return n;
    }
    return list[list.length - 1][0];
  }
}

const TAU = Math.PI * 2;
const lerp = (a: number, b: number, u: number): number => a + (b - a) * u;
const clamp01 = (u: number): number => Math.max(0, Math.min(1, u));

// ── 관문의 파수꾼 ─────────────────────────────────────────

/** 1.23 파수꾼 균열 — 월도가 땅을 치면 띠 모양으로 금이 가고 warn 초 뒤 터진다 */
interface Crack {
  x: number;
  y: number;
  dir: number;
  len: number;
  hw: number;
  t: number;
  warn: number;
  hit: boolean;
}

class Gatekeeper extends BossBrain {
  private dir = 0;
  private x0 = 0;
  private y0 = 0;
  private tx = 0;
  private ty = 0;
  private hit = false;
  /** 이어서 뛴 횟수 */
  private chain = 0;
  private cracks: Crack[] = [];

  update(h: BossHost, e: Enemy, dt: number): void {
    this.t += dt;
    const rage = e.hp < e.maxHp * 0.5 ? 0.75 : 1;
    this.updateCracks(h, e, dt);
    const dx = h.px - e.x;
    const dy = h.py - e.y;
    const d = Math.hypot(dx, dy);
    switch (this.state) {
      case 'walk': {
        const far = d > 380;
        this.play(far ? 'run' : 'move', true);
        this.animT += dt;
        this.walk(h, e, dt, far ? 1.45 : 1, 70);
        this.cd -= dt;
        if (this.cd <= 0) {
          const side = Math.abs(dx) > Math.abs(dy) * 0.8; // 찌르기는 옆으로만 (그림의 월도가 가로로 뻗는다)
          const next =
            d < 200 ? this.pick(h, [['spin', 6], ['thrust', side ? 3 : 0], ['fissure', 2]])
            : d < 440 ? this.pick(h, [['thrust', side ? 6 : 0], ['slam', 4], ['spin', side ? 0 : 2], ['fissure', 4]])
            : this.pick(h, [['slam', 5], ['fissure', 3]]);
          this.start(h, e, next);
        }
        break;
      }
      case 'fissure':
        this.fissure(h, e, rage);
        break;
      case 'spin': {
        // 월도를 들고 버틴다 → 한 바퀴
        const W = 0.62 * rage + 0.1;
        const R = 175;
        if (this.t < W) {
          this.play('spin', false);
          this.animT = 0;
          warnCircle(h.warn, e.x, e.y, R, this.t / W);
        } else {
          if (!this.hit) {
            this.hit = true;
            h.hurtIfIn(e.x, e.y, R + 12, e.damage * 1.1);
            h.sound('bladeSpin', 0.7, 1.3);
            h.sound('windBlast', 0.8, 0.9);
            h.shakeScreen(0.2, 6);
            h.burst(e.x, e.y, R, 'grey', 'air');
          }
          this.animT += dt;
          if (this.t > W + 0.5) this.finish(1.3 + h.rng.next() * 0.7, rage);
        }
        break;
      }
      case 'thrust': {
        const W = 0.55 * rage + 0.1;
        const LEN = 340;
        if (this.t < W) {
          this.play('thrust', false);
          this.animT = 6 / 14; // 웅크린 장 (마지막 장)
          warnBand(h.warn, e.x, e.y - 20, this.dir, LEN, 30, this.t / W);
        } else {
          const u = clamp01((this.t - W) / 0.2);
          if (!this.hit) {
            this.hit = true;
            this.animT = 0;
            h.hurtIfBand(this.x0, this.y0 - 20, this.dir, LEN, 34, e.damage * 1.2);
            h.sound('thrust', 0.75, 1.4);
            h.shakeScreen(0.15, 5);
          }
          // 월도를 뻗으며 앞으로 내딛는다
          e.x = this.x0 + Math.cos(this.dir) * 90 * u;
          e.y = this.y0 + Math.sin(this.dir) * 90 * u;
          this.animT = u < 0.5 ? 0 : 2 / 14;
          if (this.t > W + 0.55) this.finish(1.2 + h.rng.next() * 0.8, rage);
        }
        break;
      }
      case 'slam': {
        // 들어 올리고(0.35) → 뛰어올라 날아간다(0.7) → 내려찍는다.
        // 떨어질 자리는 들어 올리는 순간 정해진다(start) — 예전엔 뛰기 직전까지 플레이어를 따라와서
        // 0.7초에 반경 130 을 벗어나야 했다(자동 조종 17번 중 14번 맞음, 사람도 반응 시간을 빼면 못 피한다)
        const W = 0.35 * rage + 0.05;
        const AIR = 0.7;
        const R = 130;
        this.play('slam', false);
        if (this.t < W) {
          this.animT = 0;
          warnCircle(h.warn, this.tx, this.ty, R, (this.t / (W + AIR)) * 0.5);
        } else if (this.t < W + AIR) {
          const u = (this.t - W) / AIR;
          const k = u * u * (3 - 2 * u);
          e.x = lerp(this.x0, this.tx, k);
          e.y = lerp(this.y0, this.ty, k);
          this.lift = Math.sin(Math.PI * u) * 110;
          this.airborne = true;
          this.animT = 1 / 11;
          this.faceTo(this.tx - this.x0);
          warnCircle(h.warn, this.tx, this.ty, R, (this.t / (W + AIR)) * 0.5 + u * 0.5);
        } else {
          this.lift = 0;
          this.airborne = false;
          if (!this.hit) {
            this.hit = true;
            h.hurtIfIn(this.tx, this.ty, R, e.damage * 1.3);
            h.sound('hammer', 0.6, 1.5);
            h.sound('quake', 0.9, 1.1);
            h.shakeScreen(0.35, 11);
            h.fx({ name: 'slam_debris', x: this.tx, y: this.ty + 8, life: 0.6, scale: 1.1, scale1: 1.4, anchorBottom: true });
            h.burst(this.tx, this.ty, R, 'brown', 'air');
            h.burst(this.tx, this.ty, R * 0.6, 'grey', 'claw', 0.05);
            // 1.23 여진 — 체력 절반 아래이거나 2바퀴째부터: 떨어진 자리에서 X 자로 금이 간다
            if (rage < 1 || this.lap > 0) {
              for (let k = 0; k < 4; k++) {
                this.cracks.push({ x: this.tx, y: this.ty, dir: Math.PI / 4 + (k * Math.PI) / 2, len: 300, hw: 26, t: 0, warn: 0.5, hit: false });
              }
            }
          }
          this.animT = 2 / 11 + (this.t - W - AIR);
          if (this.t > W + AIR + 0.55) {
            // 2바퀴째부터는 바퀴 수만큼 한 번 더 뛰어 내려찍는다
            if (this.chain < this.lap) {
              this.chain++;
              this.start(h, e, 'slam', true);
            } else {
              this.finish(1.5 + h.rng.next() * 0.8, rage);
            }
          }
        }
        break;
      }
    }
  }

  /** 1.23 균열 — 월도를 땅에 꽂는다(들어 올리는 동안이 신호) → 부채꼴로 금이 뻗어 0.55초 뒤 터진다 */
  private fissure(h: BossHost, e: Enemy, rage: number): void {
    const W = 0.45 * rage + 0.1;
    this.play('slam', false);
    if (this.t < W) {
      this.animT = 0;
      return;
    }
    this.animT = 2 / 11 + (this.t - W);
    if (!this.hit) {
      this.hit = true;
      const n = Math.min(5, 3 + this.lap);
      for (let i = 0; i < n; i++) {
        this.cracks.push({ x: e.x, y: e.y, dir: this.dir + (i - (n - 1) / 2) * 0.36, len: 560, hw: 30, t: 0, warn: 0.55, hit: false });
      }
      h.sound('hammer', 0.7, 1.3);
      h.shakeScreen(0.3, 9);
      h.fx({ name: 'slam_debris', x: e.x + Math.cos(this.dir) * 40, y: e.y + 8, life: 0.6, scale: 1.0, scale1: 1.3, anchorBottom: true });
    }
    if (this.t > W + 0.9) this.finish(1.4 + h.rng.next() * 0.6, rage);
  }

  private updateCracks(h: BossHost, e: Enemy, dt: number): void {
    for (let i = this.cracks.length - 1; i >= 0; i--) {
      const c = this.cracks[i];
      c.t += dt;
      if (c.t < c.warn) {
        warnBand(h.warn, c.x, c.y, c.dir, c.len, c.hw, c.t / c.warn);
        continue;
      }
      if (!c.hit) {
        c.hit = true;
        h.hurtIfBand(c.x, c.y, c.dir, c.len, c.hw, e.damage);
        // 금을 따라 흙이 차례로 튄다
        for (let s = 50; s < c.len; s += 64) {
          const x = c.x + Math.cos(c.dir) * s;
          const y = c.y + Math.sin(c.dir) * s;
          h.burst(x, y, 34, 'brown', 'air', s / 1600);
          if (s % 128 < 64) h.fx({ name: 'slam_debris', x, y: y + 6, life: 0.5, scale: 0.55, scale1: 0.75, anchorBottom: true });
        }
        h.sound('quake', 1.1, 0.8);
        h.shakeScreen(0.2, 6);
      }
      if (c.t > c.warn + 0.3) this.cracks.splice(i, 1);
    }
  }

  private start(h: BossHost, e: Enemy, s: string, chained = false): void {
    this.enter(s);
    this.hit = false;
    if (!chained) this.chain = 0;
    this.x0 = e.x;
    this.y0 = e.y;
    this.dir = Math.atan2(h.py - e.y, h.px - e.x);
    this.faceTo(h.px - e.x);
    this.tx = h.px;
    this.ty = h.py;
  }

  private finish(cd: number, rage: number): void {
    this.state = 'walk';
    this.t = 0;
    this.cd = cd * rage * this.pace;
    this.lift = 0;
    this.airborne = false;
  }
}

// ── 홍련나찰 ─────────────────────────────────────────────

interface BladeRing {
  r: number;
  gaps: number[];
  spin: number;
  hit: boolean;
}

class Rakshasa extends BossBrain {
  private dir = 0;
  private hit = false;
  private tick = 0;
  private fired = false;
  private ult = 0;
  private rings: BladeRing[] = [];
  private ringT = 0;

  update(h: BossHost, e: Enemy, dt: number): void {
    this.t += dt;
    const rage = e.hp < e.maxHp * 0.5 ? 0.8 : 1;
    const dx = h.px - e.x;
    const dy = h.py - e.y;
    const d = Math.hypot(dx, dy);
    this.updateRings(h, e, dt);
    switch (this.state) {
      case 'walk': {
        // 체력 절반 · 5분의 1 에서 회전무
        if ((this.ult === 0 && e.hp < e.maxHp * 0.5) || (this.ult === 1 && e.hp < e.maxHp * 0.2)) {
          this.ult++;
          this.start(h, e, 'rise');
          break;
        }
        this.play(d > 150 ? 'move' : 'idle', true);
        this.animT += dt;
        this.walk(h, e, dt, 1, 130);
        this.cd -= dt;
        // 매혹에 걸렸으면 곧장 칼바람 — 끌려오는 플레이어를 칼 속으로
        if (h.playerCharmed && this.cd < 0.8) this.cd = 0;
        if (this.cd <= 0) {
          const next = h.playerCharmed
            ? 'storm'
            : d < 175
              ? this.pick(h, [['slash', 45], ['storm', 35], ['thrust', 20]])
              : this.pick(h, [['charm', 40], ['thrust', 35], ['storm', 25]]);
          this.start(h, e, next);
        }
        break;
      }
      case 'storm': {
        const W = 0.5 * rage;
        const R = 165;
        if (this.t < W) {
          this.play('ready', true);
          this.animT += dt;
          warnCircle(h.warn, e.x, e.y, R, this.t / W);
        } else {
          this.play('spin', true);
          this.animT += dt;
          this.walk(h, e, dt, 0.55, 0);
          warnCircle(h.warn, e.x, e.y, R, 1, 0xff4a7a);
          this.tick -= dt;
          if (this.tick <= 0) {
            this.tick = 0.26;
            h.hurtIfIn(e.x, e.y, R, e.damage * 0.45);
            h.sound('bladeSpin', 1.1 + h.rng.next() * 0.2, 0.7);
          }
          // 칼빛 둘이 둘레를 돈다
          for (let i = 0; i < 2; i++) {
            const a = this.t * 9 + i * Math.PI;
            h.stamp('rblade', 1, e.x + Math.cos(a) * R * 0.7, e.y - 30 + Math.sin(a) * R * 0.35, a + Math.PI / 2, 0.9, 0.9);
          }
          if (this.t > W + 1.3) this.finish(1.1 + h.rng.next() * 0.7, rage);
        }
        break;
      }
      case 'charm': {
        this.play('charm', false);
        this.animT += dt;
        this.faceTo(dx);
        if (!this.fired && this.t > 0.32) {
          this.fired = true;
          const base = Math.atan2(h.py - (e.y - 40), h.px - e.x);
          for (let i = -1; i <= 1; i++) {
            const a = base + i * 0.3;
            h.enemyShot({
              x: e.x + Math.cos(a) * 30, y: e.y - 40 + Math.sin(a) * 20, vx: Math.cos(a) * 205, vy: Math.sin(a) * 205,
              damage: e.damage * 0.2, radius: 11, life: 3.2, art: 'rheart_shot', scale: 0.8,
              charm: e.def.charmTime ?? 1.3, src: e, homing: 0.55, hitFx: 'rheart', upright: false,
            });
          }
          h.sound('chime', 0.8, 1.1);
          h.sound('summonBlink', 1.3, 0.7);
        }
        if (this.t > 0.85) this.finish(0.6 + h.rng.next() * 0.4, rage);
        break;
      }
      case 'thrust': {
        const W = 0.6 * rage;
        const LEN = 310;
        if (this.t < W) {
          this.play('thrust', false);
          this.animT = 0;
          this.faceTo(Math.cos(this.dir));
          for (let i = -1; i <= 1; i++) warnBand(h.warn, e.x, e.y - 24, this.dir + i * 0.3, LEN, 16, this.t / W);
        } else {
          this.animT += dt;
          if (!this.hit) {
            this.hit = true;
            for (let i = -1; i <= 1; i++) h.hurtIfBand(e.x, e.y - 24, this.dir + i * 0.3, LEN, 20, e.damage * 0.9);
            h.sound('thrust', 1.2, 1.2);
            h.sound('bladeSlash', 1.3, 0.8);
            for (let i = -1; i <= 1; i++) {
              const a = this.dir + i * 0.3;
              h.fx({ name: 'rheart_shot', x: e.x + Math.cos(a) * LEN * 0.55, y: e.y - 24 + Math.sin(a) * LEN * 0.55, life: 0.3, scale: 1.3, rot: a, alpha: 0.9 });
            }
          }
          if (this.t > W + 0.45) this.finish(1.0 + h.rng.next() * 0.7, rage);
        }
        break;
      }
      case 'slash': {
        const W = 0.42 * rage;
        const R = 180;
        const HALF = 1.05;
        if (this.t < W) {
          this.play(this.cdFlip() ? 'slash' : 'cleave', false);
          this.animT = 0;
          warnSector(h.warn, e.x, e.y - 20, R, this.dir, HALF, this.t / W);
        } else {
          this.animT += dt;
          if (!this.hit) {
            this.hit = true;
            h.hurtIfSector(e.x, e.y - 20, R, this.dir, HALF, e.damage);
            h.sound('bladeSlash', 0.9, 1.3);
            h.fx({ name: 'rblade', frame: 3, x: e.x + Math.cos(this.dir) * 90, y: e.y - 20 + Math.sin(this.dir) * 60, life: 0.28, scale: 1.4, rot: this.dir, alpha: 1 });
          }
          if (this.t > W + 0.4) this.finish(0.9 + h.rng.next() * 0.6, rage);
        }
        break;
      }
      case 'rise': {
        // 날아올라 힘을 모은다 → 회전무
        this.play('rise', false);
        this.animT += dt;
        this.lift = Math.min(1, this.t / 0.6) * 18;
        warnCircle(h.warn, e.x, e.y, 90, this.t / 0.9, 0xff4a7a);
        if (this.t > 0.9) {
          this.enter('dance');
          this.rings = [];
          this.ringT = 0;
          h.sound('summon', 0.7, 1.4);
          h.flashScreen(0.18, 0.18, 0xff5a8a);
          h.shakeScreen(0.3, 8);
        }
        break;
      }
      case 'dance': {
        this.play('dance', true);
        this.animT += dt;
        this.ringT += dt;
        // 칼 고리 셋 — 0.2 · 1.25 · 2.3 초에 하나씩, 빈틈 두 곳
        const due = [0.2, 1.25, 2.3];
        for (let k = 0; k < this.lap; k++) due.push(3.35 + k * 1.05); // 바퀴마다 고리 하나 더
        while (this.rings.length < due.length && this.ringT >= due[this.rings.length]) {
          const g0 = h.rng.next() * TAU;
          this.rings.push({ r: 50, gaps: [g0, g0 + Math.PI * (0.7 + h.rng.next() * 0.6)], spin: (h.rng.next() < 0.5 ? -1 : 1) * 0.35, hit: false });
          h.sound('bladeSpin', 0.6, 1.4);
          h.burst(e.x, e.y - 20, 70, 'red', 'ring');
        }
        if (this.t > 3.6 + this.lap * 1.05) {
          this.lift = 0;
          this.finish(1.2, rage);
        } else {
          this.lift = 18;
        }
        break;
      }
    }
  }

  private flip = false;

  private cdFlip(): boolean {
    this.flip = !this.flip;
    return this.flip;
  }

  /** 퍼지는 칼 고리 — 띠 위(빈틈 빼고)에 있으면 맞는다 */
  private updateRings(h: BossHost, e: Enemy, dt: number): void {
    if (!this.rings.length) return;
    const cx = e.x;
    const cy = e.y - 10;
    const pd = Math.hypot(h.px - cx, h.py - cy);
    const pa = Math.atan2(h.py - cy, h.px - cx);
    const GAP = 0.36;
    for (let i = this.rings.length - 1; i >= 0; i--) {
      const ring = this.rings[i];
      ring.r += 185 * dt;
      for (let k = 0; k < ring.gaps.length; k++) ring.gaps[k] += ring.spin * dt;
      if (ring.r > 620) {
        this.rings.splice(i, 1);
        continue;
      }
      warnRing(h.warn, cx, cy, ring.r, 5, ring.gaps, GAP, 0.55, 0xff3b6a);
      // 칼빛을 고리 따라 늘어놓는다
      const n = Math.max(10, Math.round((ring.r * TAU) / 60));
      for (let k = 0; k < n; k++) {
        const a = (k / n) * TAU + ring.gaps[0] * 0.25;
        if (ring.gaps.some((g) => Math.abs(Math.atan2(Math.sin(a - g), Math.cos(a - g))) < GAP + 0.08)) continue;
        h.stamp('rblade', 1, cx + Math.cos(a) * ring.r, cy + Math.sin(a) * ring.r, a + Math.PI, 0.55, 0.9);
      }
      if (!ring.hit && Math.abs(pd - ring.r) < 20) {
        const inGap = ring.gaps.some((g) => Math.abs(Math.atan2(Math.sin(pa - g), Math.cos(pa - g))) < GAP);
        if (!inGap) {
          ring.hit = true;
          h.hurtIfIn(h.px, h.py, 1, e.damage * 0.7);
        }
      }
    }
  }

  private start(h: BossHost, e: Enemy, s: string): void {
    this.enter(s);
    this.hit = false;
    this.fired = false;
    this.tick = 0;
    this.dir = Math.atan2(h.py - e.y, h.px - e.x);
    this.faceTo(h.px - e.x);
  }

  private finish(cd: number, rage: number): void {
    this.state = 'walk';
    this.t = 0;
    this.cd = cd * rage * this.pace;
  }
}

// ── 공손찬 ───────────────────────────────────────────────

interface Pillar {
  x: number;
  y: number;
  t: number;
  hit: boolean;
}

class King extends BossBrain {
  private dir = 0;
  private hit = false;
  private pillars: Pillar[] = [];
  private spawned = 0;
  private serpentDir = 1;
  private bandY = 0;
  private bandX = 0;
  private wave = 0;

  update(h: BossHost, e: Enemy, dt: number): void {
    this.t += dt;
    const rage = e.hp < e.maxHp * 0.5 ? 0.75 : 1;
    const dx = h.px - e.x;
    const dy = h.py - e.y;
    const d = Math.hypot(dx, dy);
    this.updatePillars(h, e, dt);
    switch (this.state) {
      case 'walk': {
        this.play('move', true);
        this.animT += dt;
        this.walk(h, e, dt, 1, 110);
        this.cd -= dt;
        if (this.cd <= 0) {
          // 1.23 화우 — 체력 절반 아래이거나 2바퀴째부터
          const rain = rage < 1 || this.lap > 0 ? 35 : 0;
          const next = d < 300
            ? this.pick(h, [['crescent', 40], ['pillars', 30], ['orbs', 30], ['rain', rain]])
            : this.pick(h, [['serpent', 35], ['pillars', 35], ['orbs', 30], ['rain', rain]]);
          this.start(h, e, next);
        }
        break;
      }
      case 'crescent': {
        const W = 0.75 * rage + 0.1;
        const R = 300;
        const HALF = 1.3;
        if (this.t < W) {
          this.play('move', true);
          this.animT = 0;
          warnSector(h.warn, e.x, e.y - 30, R, this.dir, HALF, this.t / W);
        } else {
          this.play('crescent', false);
          this.animT = 0;
          // 그림의 초승달은 왼쪽 아래로 휘두른다 — 플레이어가 오른쪽이면 뒤집는다
          this.face = Math.cos(this.dir) > 0 ? -1 : 1;
          if (!this.hit) {
            this.hit = true;
            h.hurtIfSector(e.x, e.y - 30, R, this.dir, HALF, e.damage * 0.9);
            h.sound('boomFire', 0.7, 1.4);
            h.sound('windBlast', 0.6, 1.1);
            h.shakeScreen(0.3, 9);
            for (let k = 0; k < 6; k++) {
              const a = this.dir - HALF + (HALF * 2 * (k + 0.5)) / 6;
              h.burst(e.x + Math.cos(a) * R * 0.7, e.y - 30 + Math.sin(a) * R * 0.7, 46, 'red', 'fire', k * 0.03);
            }
          }
          if (this.t > W + 0.5) this.finish(1.6 + h.rng.next() * 0.8, rage);
        }
        break;
      }
      case 'pillars': {
        // 발밑부터 차례로 솟는 불기둥
        this.play('pillars', false);
        this.animT = 0;
        const n = (rage < 1 ? 7 : 5) + this.lap * 2;
        const every = 0.26;
        while (this.spawned < n && this.t >= this.spawned * every + 0.15) {
          const lead = this.spawned === 0 ? 0 : 60 + h.rng.next() * 110;
          const a = h.rng.next() * TAU;
          this.pillars.push({ x: h.px + Math.cos(a) * lead, y: h.py + Math.sin(a) * lead * 0.7, t: 0, hit: false });
          this.spawned++;
        }
        if (this.t > n * every + 0.9) this.finish(1.4 + h.rng.next() * 0.8, rage);
        break;
      }
      case 'orbs': {
        // 둘레를 도는 불구슬 여덟 → 사방으로 (두 번, 어긋나게)
        this.play('orbs', false);
        this.animT = 0;
        const waves = (rage < 1 ? 3 : 2) + this.lap;
        while (this.wave < waves && this.t >= this.wave * 0.55 + 0.2) {
          const off = (this.wave * Math.PI) / 8;
          for (let k = 0; k < 8; k++) {
            const a = off + (k / 8) * TAU;
            h.enemyShot({
              x: e.x, y: e.y - 60, vx: 0, vy: 0, damage: e.damage * 0.4, radius: 12, life: 4.2,
              art: 'fire_orb', scale: 1.1, spin: 6, src: e,
              orbit: 0.8, orbitR: 120, orbitA: a, orbitW: 2.6, speed: 215, hitFx: undefined,
            });
          }
          h.sound('flame', 0.7, 1.2);
          this.wave++;
        }
        if (this.t > waves * 0.55 + 1.2) this.finish(1.5 + h.rng.next() * 0.8, rage);
        break;
      }
      case 'rain': {
        // 1.23 화우 — 불구슬을 들어 올리면 플레이어 둘레에 불기둥이 비처럼 차례로 떨어진다
        this.play('orbs', false);
        this.animT = 0;
        const n = 12 + this.lap * 4 + (rage < 1 ? 4 : 0);
        if (this.spawned === 0 && this.t > 0.35) {
          this.spawned = 1;
          // 첫 기둥은 발밑 — 나머지는 둘레 60~380px 에 흩어져 0.08초 간격으로
          this.pillars.push({ x: h.px, y: h.py, t: 0, hit: false });
          for (let i = 1; i < n; i++) {
            const a = h.rng.next() * TAU;
            const r = 60 + h.rng.next() * 320;
            this.pillars.push({ x: h.px + Math.cos(a) * r, y: h.py + Math.sin(a) * r * 0.75, t: -i * 0.08, hit: false });
          }
          h.sound('flame', 0.6, 1.3);
          h.flashScreen(0.12, 0.15, 0xff6020);
          h.shakeScreen(0.4, 5);
        }
        if (this.t > 0.35 + n * 0.08 + 1.2) this.finish(1.6 + h.rng.next() * 0.8, rage);
        break;
      }
      case 'serpent': {
        // 가로 띠 경고 → 이무기가 화면을 가로지른다
        const W = 1.0 * rage + 0.1;
        const HALF = 760;
        const HW = 48;
        this.play('move', true);
        this.animT += dt * 0.5;
        if (this.t < W) {
          this.bandY = h.py;
          this.bandX = h.px;
          warnBand(h.warn, this.bandX - HALF * this.serpentDir, this.bandY, this.serpentDir > 0 ? 0 : Math.PI, HALF * 2, HW, this.t / W);
        } else {
          const u = (this.t - W) / 0.85;
          const sx = this.bandX + (-HALF + HALF * 2 * u) * this.serpentDir;
          // 그림의 머리는 아래쪽 끝이다 — 머리가 띠 위를 지나가게 몸을 위로 올려 그린다
          h.stamp('serpent', 0, sx, this.bandY - 62, 0, this.serpentDir > 0 ? -1.15 : 1.15, 1);
          if (!this.hit && Math.abs(sx - h.px) < 110 && Math.abs(h.py - this.bandY) < HW + 12) {
            this.hit = h.hurtIfIn(h.px, h.py, 1, e.damage) || this.hit;
          }
          if (u > 0.1 && u < 0.9 && h.rng.next() < 0.5) h.burst(sx, this.bandY + (h.rng.next() - 0.5) * 60, 30, 'red', 'fire');
          if (!this.hit && this.t - dt < W) {
            h.sound('windBlast', 0.5, 1.5);
            h.sound('boomFire', 0.5, 1.0);
            h.shakeScreen(0.6, 6);
          }
          if (u >= 1) this.finish(1.5 + h.rng.next() * 0.8, rage);
        }
        break;
      }
    }
  }

  private updatePillars(h: BossHost, e: Enemy, dt: number): void {
    const WARN = 0.85;
    const R = 58;
    for (let i = this.pillars.length - 1; i >= 0; i--) {
      const p = this.pillars[i];
      p.t += dt;
      if (p.t < 0) continue; // 화우: 아직 차례가 안 왔다
      if (p.t < WARN) {
        warnCircle(h.warn, p.x, p.y, R, p.t / WARN);
        continue;
      }
      if (!p.hit) {
        p.hit = true;
        h.hurtIfIn(p.x, p.y, R, e.damage * 0.8);
        h.fx({ name: 'pillar', x: p.x, y: p.y + 10, life: 0.7, scale: 0.4, scale1: 1.25, anchorBottom: true });
        h.burst(p.x, p.y, R, 'red', 'fire');
        h.sound('pillar', 0.8 + h.rng.next() * 0.2, 0.9);
      }
      if (p.t > WARN + 0.1) this.pillars.splice(i, 1);
    }
  }

  private start(h: BossHost, e: Enemy, s: string): void {
    this.enter(s);
    this.hit = false;
    this.spawned = 0;
    this.wave = 0;
    this.dir = Math.atan2(h.py - (e.y - 30), h.px - e.x);
    this.serpentDir = h.rng.next() < 0.5 ? 1 : -1;
  }

  private finish(cd: number, rage: number): void {
    this.state = 'walk';
    this.t = 0;
    this.cd = cd * rage * this.pace;
  }
}

// ── 류대신 ───────────────────────────────────────────────
// 1.23 흰 머리의 혈검사. 사람 크기(몸 약 100px)라 거한들처럼 버티지 않고 빠르게 들어오고 빠지며, 기술이 화면을 크게 가른다.
//  혈월참: 발검 → 핏빛 초승달 셋(바퀴·각성마다 늘어난다)이 부채꼴로 날아간다
//  섬광 일섬: 긴 띠 경고 → 눈 깜짝할 새 그 줄을 가르고 지나간다, 이어서 또 (바퀴·각성마다 한 번 더)
//  혈검진: 칼날 구체를 두르고(원 경고) 터뜨린다 — 가까이는 베이고, 칼날이 사방으로 날아간다(사이로 빠진다)
//  혈룡파: 손을 모아 화면 끝까지 닿는 핏빛 광선. 각성하면 쏘는 동안 플레이어 쪽으로 천천히 휩쓴다
//  혈화: 발밑에서 핏빛 가시가 줄지어 솟으며 달려온다(바퀴마다 줄이 는다)
//  잔영 베기: 연기처럼 사라져 플레이어 반대편에 나타나 벤다
//  혈신 강림: 체력 절반에서 한 번 — 뛰어올라 떨어지며 둘레에 가시가 솟고, 그 뒤로 모든 게 빨라진다

interface Spike {
  x: number;
  y: number;
  /** 0 부터 경고, WARN 에 솟는다 (음수면 아직 차례가 안 왔다) */
  t: number;
  /** 솟을 때 때리나 (혈신 강림의 가시 고리는 떨어진 자리 원이 이미 때렸다) */
  dmg: boolean;
  done: boolean;
}

interface Trail {
  x: number;
  y: number;
  dir: number;
  len: number;
  t: number;
}

class Ryu extends BossBrain {
  private dir = 0;
  private hit = false;
  private step = 0;
  private chain = 0;
  private rage = false;
  private x0 = 0;
  private y0 = 0;
  private tx = 0;
  private ty = 0;
  private len = 0;
  private tick = 0;
  private smokeT = 0;
  private spikes: Spike[] = [];
  private trails: Trail[] = [];

  update(h: BossHost, e: Enemy, dt: number): void {
    this.t += dt;
    const k = this.rage ? 0.72 : 1;
    const dx = h.px - e.x;
    const dy = h.py - e.y;
    const d = Math.hypot(dx, dy);
    this.updateSpikes(h, e, dt);
    this.updateTrails(h, dt);
    // 각성한 뒤로는 발밑에서 핏빛 연기가 피어오른다
    if (this.rage) {
      this.smokeT -= dt;
      if (this.smokeT <= 0) {
        this.smokeT = 0.32;
        h.fx({ name: 'rsmoke', x: e.x + (h.rng.next() - 0.5) * 30, y: e.y + 10, life: 0.9, scale: 0.55, scale1: 0.8, anchorBottom: true, alpha: 0.55, vy: -18 });
      }
    }
    switch (this.state) {
      case 'walk': {
        if (!this.rage && e.hp < e.maxHp * 0.5) {
          this.start(h, e, 'awaken');
          break;
        }
        this.play(d > 140 ? 'move' : 'stand', true);
        this.animT += dt;
        this.walk(h, e, dt, d > 420 ? 1.35 : 1, 120);
        this.cd -= dt;
        if (this.cd <= 0) {
          const next =
            d > 420 ? this.pick(h, [['dash', 4], ['beam', 3], ['crescent', 3], ['spikes', 3], ['blink', 2]])
            : d > 190 ? this.pick(h, [['crescent', 4], ['spikes', 3], ['blink', 3], ['aura', 2], ['dash', 3], ['beam', 2]])
            : this.pick(h, [['aura', 4], ['blink', 3], ['slash', 4]]);
          this.start(h, e, next);
        }
        break;
      }
      case 'crescent': {
        // 발검 — 부채꼴 띠 경고가 차오르면 초승달이 날아간다
        const W = 0.55 * k + 0.1;
        const n = Math.min(9, 3 + this.lap * 2 + (this.rage ? 2 : 0));
        const gap = n > 5 ? 0.2 : 0.3;
        if (this.t < W) {
          this.play('draw', false);
          this.animT = (this.t / W) * (2 / 8);
          for (let i = 0; i < n; i++) warnBand(h.warn, e.x, e.y - 30, this.dir + (i - (n - 1) / 2) * gap, 560, 20, this.t / W);
        } else {
          this.play('crescent', false);
          this.animT += dt;
          if (this.step === 0) {
            this.step = 1;
            this.volley(h, e, n, gap, 0);
          }
          // 각성하면 반 칸 어긋나게 한 번 더
          if (this.rage && this.step === 1 && this.t > W + 0.32) {
            this.step = 2;
            this.volley(h, e, n, gap, gap / 2);
          }
          if (this.t > W + (this.rage ? 0.8 : 0.55)) this.finish(1.0 + h.rng.next() * 0.6, k);
        }
        break;
      }
      case 'dash': {
        // 섬광 일섬 — 띠 경고 → 0.16초에 줄을 가른다 → 잠깐 멈췄다 또
        const W = (this.chain > 0 ? 0.36 : 0.55) * k + 0.08;
        const RUN = 0.16;
        if (this.t < W) {
          this.play('dashpose', false);
          this.animT = 0;
          this.faceTo(Math.cos(this.dir));
          warnBand(h.warn, this.x0, this.y0 - 18, this.dir, this.len, 36, this.t / W);
        } else if (this.t < W + RUN) {
          this.play('dash', false);
          this.animT = 0;
          const u = (this.t - W) / RUN;
          e.x = this.x0 + Math.cos(this.dir) * this.len * u;
          e.y = this.y0 + Math.sin(this.dir) * this.len * u;
          if (!this.hit) {
            this.hit = true;
            h.hurtIfBand(this.x0, this.y0 - 18, this.dir, this.len, 40, e.damage * 1.1);
            this.trails.push({ x: this.x0, y: this.y0 - 18, dir: this.dir, len: this.len, t: 0 });
            h.sound('ashSwing', 1.25, 1.1);
            h.sound('windBlast', 1.3, 0.8);
            h.shakeScreen(0.18, 6);
          }
        } else {
          this.play('thrust', false);
          this.animT = 0;
          if (this.t > W + RUN + 0.2) {
            if (this.chain < 1 + this.lap + (this.rage ? 1 : 0)) {
              this.chain++;
              this.start(h, e, 'dash', true);
            } else {
              this.finish(1.1 + h.rng.next() * 0.6, k);
            }
          }
        }
        break;
      }
      case 'aura': {
        // 혈검진 — 칼날 구체를 두르고(원 경고) → 터뜨린다: 둘레는 베이고 칼날이 사방으로
        const W = 0.8 * k + 0.1;
        const R = 150;
        this.play('aura', true);
        this.animT += dt;
        if (this.t < W) {
          warnCircle(h.warn, e.x, e.y, R, this.t / W, 0xff2a4a);
          this.walk(h, e, dt, 0.35, 0);
        } else {
          if (this.step === 0) {
            this.step = 1;
            h.hurtIfIn(e.x, e.y, R, e.damage * 0.9);
            this.ring(h, e, this.rage ? 18 : 14, 0);
            h.burst(e.x, e.y - 20, R, 'red', 'ring');
            h.sound('bladeSpin', 0.8, 1.3);
            h.sound('shatter', 0.7, 0.9);
            h.shakeScreen(0.25, 7);
          }
          // 2바퀴째부터 반 칸 어긋난 고리가 한 번 더
          if (this.lap > 0 && this.step === 1 && this.t > W + 0.35) {
            this.step = 2;
            this.ring(h, e, this.rage ? 18 : 14, 0.5);
            h.sound('bladeSpin', 1.0, 1.0);
          }
          if (this.t > W + 0.7) this.finish(1.0 + h.rng.next() * 0.6, k);
        }
        break;
      }
      case 'beam': {
        // 혈룡파 — 쏠 쪽은 손을 모을 때 정한다. 각성하면 쏘는 동안 플레이어 쪽으로 휩쓴다
        const W = 1.0 * k + 0.2;
        const FIRE = 0.65;
        const LEN = 1150;
        const HW = 56;
        const ox = e.x;
        const oy = e.y - 34;
        if (this.t < W) {
          this.play('cast', false);
          this.animT = (this.t / W) * (2 / 9);
          this.faceTo(Math.cos(this.dir));
          warnBand(h.warn, ox, oy, this.dir, LEN, HW, this.t / W);
          if (this.step === 0 && this.t > W * 0.35) {
            this.step = 1;
            h.sound('summonRise', 0.9, 1.0);
          }
        } else if (this.t < W + FIRE) {
          this.play('aura', true);
          this.animT += dt;
          if (this.step < 2) {
            this.step = 2;
            h.sound('boomFire', 0.6, 1.4);
            h.sound('windBlast', 0.5, 1.3);
            h.flashScreen(0.14, 0.2, 0xff2040);
            h.shakeScreen(FIRE, 7);
            h.fx({ name: 'rsplash', x: ox + Math.cos(this.dir) * 40, y: oy + 20, life: 0.5, scale: 0.8, scale1: 1.2, anchorBottom: true });
          }
          if (this.rage || this.lap > 0) {
            // 플레이어 쪽으로 초당 0.5rad 까지 휩쓴다
            const want = Math.atan2(h.py - oy, h.px - ox);
            const diff = Math.atan2(Math.sin(want - this.dir), Math.cos(want - this.dir));
            this.dir += Math.max(-0.5 * dt, Math.min(0.5 * dt, diff));
          }
          const u = (this.t - W) / FIRE;
          const thick = (u < 0.15 ? u / 0.15 : u > 0.8 ? (1 - u) / 0.2 : 1) * (1 + 0.1 * Math.sin(this.t * 60));
          const cx = ox + Math.cos(this.dir) * LEN * 0.5;
          const cy = oy + Math.sin(this.dir) * LEN * 0.5;
          h.stamp('rbeam', 0, cx, cy, this.dir, LEN / 280, 1, 1.3 * thick);
          h.stamp('rbeam', 0, cx, cy, this.dir, LEN / 280, 0.7, 0.55 * thick);
          warnBand(h.warn, ox, oy, this.dir, LEN, HW, 1, 0xff4a6a);
          this.tick -= dt;
          if (this.tick <= 0) {
            this.tick = 0.15;
            h.hurtIfBand(ox, oy, this.dir, LEN, HW, e.damage * 0.35);
          }
        } else {
          this.play('stand', true);
          this.animT += dt;
          if (this.t > W + FIRE + 0.35) this.finish(1.3 + h.rng.next() * 0.6, k);
        }
        break;
      }
      case 'spikes': {
        // 혈화 — 손을 들면 발밑부터 가시가 줄지어 솟으며 플레이어 쪽으로 달려온다
        this.play('cast', false);
        this.animT += dt;
        const lines = Math.min(5, 1 + this.lap + (this.rage ? 2 : 0));
        const N = 9;
        if (this.step === 0 && this.t > 0.3) {
          this.step = 1;
          for (let l = 0; l < lines; l++) {
            const a = this.dir + (l - (lines - 1) / 2) * 0.42;
            for (let i = 0; i < N; i++) {
              const r = 70 + i * 74;
              // t 가 음수면 아직 경고도 안 뜬다 — 차례로 달려온다
              this.spikes.push({ x: e.x + Math.cos(a) * r, y: e.y + Math.sin(a) * r * 0.85, t: -i * 0.075, dmg: true, done: false });
            }
          }
          h.sound('quake', 1.3, 0.9);
        }
        if (this.t > 0.3 + N * 0.075 + 0.8) this.finish(1.0 + h.rng.next() * 0.6, k);
        break;
      }
      case 'blink': {
        // 잔영 베기 — 연기처럼 사라져 플레이어 반대편에 나타나 벤다
        const OUT = 0.24;
        const W = 0.38 * k + 0.06;
        const R = 175;
        const HALF = 1.15;
        if (this.t < OUT) {
          this.play('blink', false);
          this.animT = 0;
          if (this.step === 0) {
            this.step = 1;
            h.fx({ name: 'rsmoke', x: e.x, y: e.y + 10, life: 0.6, scale: 0.8, scale1: 1.1, anchorBottom: true });
            h.sound('summonBlink', 0.7, 0.9);
          }
        } else {
          if (this.step === 1) {
            this.step = 2;
            // 플레이어를 사이에 두고 반대편 130px
            const bx = h.px - e.x;
            const by = h.py - e.y;
            const bd = Math.hypot(bx, by) || 1;
            e.x = h.px + (bx / bd) * 130;
            e.y = h.py + (by / bd) * 130;
            this.dir = Math.atan2(h.py - e.y, h.px - e.x);
            this.faceTo(h.px - e.x);
            h.fx({ name: 'rsmoke', x: e.x, y: e.y + 10, life: 0.6, scale: 0.8, scale1: 1.1, anchorBottom: true });
          }
          if (this.t < OUT + W) {
            this.play('ready', false);
            this.animT = 0;
            warnSector(h.warn, e.x, e.y - 20, R, this.dir, HALF, (this.t - OUT) / W);
          } else {
            this.play('slash', false);
            this.animT = Math.min(1 / 14, this.t - OUT - W);
            if (!this.hit) {
              this.hit = true;
              h.hurtIfSector(e.x, e.y - 20, R, this.dir, HALF, e.damage);
              h.fx({ name: 'rcrescent_big', x: e.x + Math.cos(this.dir) * 80, y: e.y - 20 + Math.sin(this.dir) * 60, life: 0.32, scale: 1.3, scale1: 1.6, rot: this.dir + Math.PI, alpha: 1 });
              h.sound('ashSwing', 1.0, 1.2);
            }
            if (this.t > OUT + W + 0.4) this.finish(1.0 + h.rng.next() * 0.5, k);
          }
        }
        break;
      }
      case 'slash': {
        // 가까우면 두 번 벤다 (두 번째는 다시 노린다)
        const W1 = 0.34 * k + 0.06;
        const W2 = 0.26 * k + 0.05;
        const R = 170;
        const HALF = 1.1;
        const t2 = W1 + 0.2;
        if (this.t < W1) {
          this.play('ready', false);
          this.animT = 0;
          warnSector(h.warn, e.x, e.y - 20, R, this.dir, HALF, this.t / W1);
        } else if (this.t < t2) {
          this.play('slash', false);
          this.animT = 0;
          if (this.step === 0) {
            this.step = 1;
            h.hurtIfSector(e.x, e.y - 20, R, this.dir, HALF, e.damage * 0.8);
            h.fx({ name: 'rcrescent_big', x: e.x + Math.cos(this.dir) * 80, y: e.y - 20 + Math.sin(this.dir) * 60, life: 0.3, scale: 1.2, scale1: 1.5, rot: this.dir + Math.PI });
            h.sound('ashSwing', 1.15, 1.0);
          }
        } else if (this.t < t2 + W2) {
          if (this.step === 1) {
            this.step = 2;
            this.dir = Math.atan2(h.py - e.y, h.px - e.x);
            this.faceTo(h.px - e.x);
          }
          this.play('ready', false);
          this.animT = 0;
          warnSector(h.warn, e.x, e.y - 20, R, this.dir, HALF, (this.t - t2) / W2);
        } else {
          this.play('slash', false);
          this.animT = 1 / 14;
          if (this.step === 2) {
            this.step = 3;
            h.hurtIfSector(e.x, e.y - 20, R, this.dir, HALF, e.damage * 0.8);
            h.fx({ name: 'rcrescent_big', x: e.x + Math.cos(this.dir) * 80, y: e.y - 20 + Math.sin(this.dir) * 60, life: 0.3, scale: 1.2, scale1: 1.5, rot: this.dir + Math.PI, flipX: true });
            h.sound('ashSwing', 0.95, 1.0);
          }
          if (this.t > t2 + W2 + 0.4) this.finish(0.9 + h.rng.next() * 0.5, k);
        }
        break;
      }
      case 'awaken': {
        // 혈신 강림 — 뛰어올라(떨어질 자리 원 경고) 떨어지며 둘레에 가시가 솟는다. 그 뒤로 모든 게 빨라진다
        // 떨어질 자리는 뛰어오를 때 정한다 — 한가운데서도 1.1초면 160px 밖으로 나갈 수 있게 (내려찍기와 같은 규칙)
        const RISE = 1.1;
        const R = 160;
        this.play('leap', false);
        if (this.t < RISE) {
          this.animT = 0;
          const u = this.t / RISE;
          this.lift = Math.sin(Math.PI * Math.min(1, u * 1.1)) * 120;
          this.airborne = true;
          e.x = lerp(this.x0, this.tx, u * u);
          e.y = lerp(this.y0, this.ty, u * u);
          warnCircle(h.warn, this.tx, this.ty, R, u, 0xff2a4a);
          if (this.step === 0) {
            this.step = 1;
            h.sound('summon', 0.6, 1.3);
            h.flashScreen(0.15, 0.2, 0xff2040);
          }
        } else {
          this.lift = 0;
          this.airborne = false;
          this.animT = 1 / 10;
          if (this.step === 1) {
            this.step = 2;
            this.rage = true;
            h.hurtIfIn(this.tx, this.ty, R, e.damage * 1.1);
            h.shakeScreen(0.5, 13);
            h.flashScreen(0.2, 0.3, 0xff2040);
            h.sound('boomFire', 0.5, 1.5);
            h.sound('quake', 0.8, 1.2);
            h.fx({ name: 'rcrescent_big', x: this.tx, y: this.ty - 20, life: 0.5, scale: 2.2, scale1: 2.8, alpha: 1 });
            h.burst(this.tx, this.ty, R, 'red', 'ring');
            // 둘레에 가시 고리 — 곧바로 솟는다(경고는 떨어질 자리 원이 대신)
            for (let i = 0; i < 12; i++) {
              const a = (i / 12) * TAU;
              this.spikes.push({ x: this.tx + Math.cos(a) * R * 0.9, y: this.ty + Math.sin(a) * R * 0.75, t: 0.55, dmg: false, done: false });
            }
          }
          if (this.t > RISE + 0.8) this.finish(0.8, k);
        }
        break;
      }
    }
  }

  /** 초승달 n 개를 부채꼴로 (off = 칸 어긋남) */
  private volley(h: BossHost, e: Enemy, n: number, gap: number, off: number): void {
    for (let i = 0; i < n; i++) {
      const a = this.dir + (i - (n - 1) / 2) * gap + off;
      h.enemyShot({
        x: e.x + Math.cos(a) * 40, y: e.y - 30 + Math.sin(a) * 30, vx: Math.cos(a) * 520, vy: Math.sin(a) * 520,
        damage: e.damage * 0.7, radius: 22, life: 1.5, art: 'rcrescent', scale: 1.7, src: e, hitFx: 'rsplash',
      });
    }
    h.sound('ashSwing', 0.9, 1.3);
    h.sound('windBlast', 1.1, 0.9);
    h.shakeScreen(0.2, 6);
  }

  /** 칼날 고리 — n 자루가 사방으로 (off = 칸 어긋남 0~1) */
  private ring(h: BossHost, e: Enemy, n: number, off: number): void {
    for (let i = 0; i < n; i++) {
      const a = ((i + off) / n) * TAU;
      h.enemyShot({
        x: e.x + Math.cos(a) * 40, y: e.y - 24 + Math.sin(a) * 34, vx: Math.cos(a) * 300, vy: Math.sin(a) * 300,
        damage: e.damage * 0.4, radius: 13, life: 2.2, art: 'rcrescent', scale: 0.8, spin: 9, src: e,
      });
    }
  }

  private updateSpikes(h: BossHost, e: Enemy, dt: number): void {
    const WARN = 0.55;
    const R = 46;
    for (let i = this.spikes.length - 1; i >= 0; i--) {
      const s = this.spikes[i];
      s.t += dt;
      if (s.t < 0) continue;
      if (s.t < WARN) {
        warnCircle(h.warn, s.x, s.y, R, s.t / WARN, 0xff2a4a);
        continue;
      }
      if (!s.done) {
        s.done = true;
        if (s.dmg) h.hurtIfIn(s.x, s.y, R, e.damage * 0.8);
        h.fx({ name: 'rspike', x: s.x, y: s.y + 8, life: 0.6, scale: 0.7, scale1: 1.0, anchorBottom: true });
        h.sound('pillar', 1.25 + h.rng.next() * 0.2, 0.5);
      }
      if (s.t > WARN + 0.1) this.spikes.splice(i, 1);
    }
  }

  /** 섬광 일섬이 지나간 자리의 핏빛 궤적 — 0.5초에 걸쳐 가늘어지며 사라진다 */
  private updateTrails(h: BossHost, dt: number): void {
    for (let i = this.trails.length - 1; i >= 0; i--) {
      const tr = this.trails[i];
      tr.t += dt;
      if (tr.t > 0.5) {
        this.trails.splice(i, 1);
        continue;
      }
      const u = tr.t / 0.5;
      h.stamp('rbeam', 0, tr.x + Math.cos(tr.dir) * tr.len * 0.5, tr.y + Math.sin(tr.dir) * tr.len * 0.5, tr.dir, tr.len / 280, 1 - u, 0.42 * (1 - u * 0.7));
    }
  }

  private start(h: BossHost, e: Enemy, s: string, chained = false): void {
    this.enter(s);
    this.hit = false;
    this.step = 0;
    this.tick = 0;
    if (!chained) this.chain = 0;
    this.x0 = e.x;
    this.y0 = e.y;
    this.dir = Math.atan2(h.py - e.y, h.px - e.x);
    this.faceTo(h.px - e.x);
    const d = Math.hypot(h.px - e.x, h.py - e.y);
    // 섬광 일섬은 플레이어를 지나 160px 더 (360 ~ 720)
    this.len = Math.max(360, Math.min(720, d + 160));
    this.tx = h.px;
    this.ty = h.py;
  }

  private finish(cd: number, k: number): void {
    this.state = 'walk';
    this.t = 0;
    this.cd = cd * k * this.pace;
    this.lift = 0;
    this.airborne = false;
  }
}

/** lap = 몇 바퀴째 (0 = 첫 등장) — 바퀴마다 더 잦고 촘촘해진다 */
export function makeBrain(pattern: string | undefined, lap = 0): BossBrain | null {
  let b: BossBrain | null = null;
  if (pattern === 'gatekeeper') b = new Gatekeeper();
  else if (pattern === 'rakshasa') b = new Rakshasa();
  else if (pattern === 'king') b = new King();
  else if (pattern === 'ryu') b = new Ryu();
  if (b) b.lap = lap;
  return b;
}
